import { useUserProfile } from "../../hooks/useUserProfile";
import TelegramRegistration from "./profilePages/TelegramRegistration";
import { useEffect, useMemo, useRef, useState } from "react";
import SearchPreferences from "./profilePages/SearchPreferences";
import ProfileDetails from "./profilePages/ProfileDetails";
import PersonalProfile from "./profilePages/PersonalProfile";
import SubscriptionManagement from "./profilePages/SubscriptionManagement";
import { Button, Divider, Menu } from "react-daisyui";
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout, { AppPageLayout } from "./components/PageLayout";
import ImportantFactors from "./profilePages/ImportantFactors";
import { Search, Star, MessageCircle, CreditCard, User, FileText, LogOut, Mail } from 'lucide-react';
import FeedbackDialog from "./components/FeedbackDialog";
import { useAuth } from "../../modules/authentication/AuthProvider";
import { useTranslation } from 'react-i18next';
import routes from "../../modules/routes/Routes";
import ErrorBoundary from "./components/ErrorBoundary";
import EmailNotification from "./profilePages/EmailNotification";
import SearchPosts from "./profilePages/NewspaperPost";

export const useProfilePages = () => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();

    return [
        { 
            key: 'SearchPreferences', 
            name: t('pages.profilePage.menu.searchPreferences'), 
            icon: <Search size={18} />, 
            component: () => <SearchPreferences />
        },
        /*{ 
            key: 'ImportantFactors', 
            name: t('pages.profilePage.menu.importantFactors'), 
            icon: <Star size={18} />, 
            component: () => <ImportantFactors />
        },*/
        { 
            key: 'PersonalProfile', 
            name: t('pages.profilePage.menu.profile'), 
            icon: <User size={18} />, 
            component: () => <PersonalProfile />
        },
        /*{ 
            key: 'ProfileDetails', 
            name: t('pages.profilePage.menu.additionalInfo'), 
            icon: <FileText size={18} />, 
            component: () => <ProfileDetails />
        },*/
        
        //@FEATURE-FLAG
        ...(currentUser?.email?.includes('niklasgromann') || currentUser?.email?.includes('demler') || currentUser?.email?.includes('@test') ? [
            { 
                key: 'divider-1',
                name: 'Divider',
                component: () => <div className="divider"></div>
            },
            { 
                key: 'SearchPosts', 
                name: t('pages.profilePage.menu.searchPosts'), 
                icon: <Search size={18} />, 
                component: () => <SearchPosts />
            },
        ] as const : []),

        { 
            key: 'divider-2',
            name: 'Divider',
            component: () => <div className="divider"></div>
        },
        { 
            key: 'TelegramRegistration', 
            name: t('pages.profilePage.menu.telegram'), 
            icon: <MessageCircle size={18} />, 
            component: () => <TelegramRegistration />
        },
        { 
            key: 'EmailNotification', 
            name: t('pages.profilePage.menu.email'), 
            icon: <Mail size={18} />, 
            component: () => <EmailNotification />
        },
        { 
            key: 'SubscriptionManagement', 
            name: t('pages.profilePage.menu.subscription'), 
            icon: <CreditCard size={18} />, 
            component: () => <SubscriptionManagement />
        },
    ] as const;
};

const ProfilePage: React.FC<{ page?: ReturnType<typeof useProfilePages>[number]['key'] }> = ({ page = 'SearchPreferences' }) => {
    const { t } = useTranslation();
    const { logout } = useAuth();
    const { userProfile, localProfile, hasPendingChanges, updateUserProfile } = useUserProfile();
    const [searchParams] = useSearchParams();
    const pageContentRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const pages = useProfilePages();

    useEffect(() => {
        console.log({navigate});
    }, [navigate]);
    
    const currentPage = useMemo(() => {
        const redirectStatus = searchParams.get('redirect_status');
        return redirectStatus === 'succeeded' ? 'SubscriptionManagement' : page;
    }, [searchParams, page]);

    if (userProfile === undefined || localProfile === undefined) {
        return null; // or return a loading indicator
    }

    const renderPage = () => {
        const pageComponent = pages.find(p => p.key === currentPage);
        return pageComponent ? pageComponent.component() : null;
    };

    const handleSave = () => {
        if (hasPendingChanges) {
            updateUserProfile(localProfile);
        }
    };

    const openPage = (pageKey: typeof pages[number]['key']) => {
        navigate(Object.values(routes.app.profile).filter(p => p.key === pageKey)[0].build());
        if (window.innerWidth <= 768 && pageContentRef.current) {
            pageContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
    };

    return (
        <AppPageLayout
            currentPage={currentPage}
        >
            <div className="flex flex-col md:flex-row">
                <nav className="hidden md:block w-full md:w-64 md:pr-8 md:border-r md:border-base-300 md:mr-8 mb-4 md:mb-0">
                    <Menu className="menu-compact flex flex-col md:flex-col overflow-y-auto md:overflow-x-visible">
                        {pages.map(page => (
                            page.key.includes('divider') ? (
                                <Divider key={page.key} className="my-1 mx-2" />
                            ) : (
                                <Menu.Item key={page.key} className="flex-shrink-0">
                                    <button  
                                        color="ghost"
                                        className={`py-2 px-4 w-full text-left my-1 flex items-center ${currentPage === page.key ? 'bg-base-200 font-medium' : ''}`}
                                        onClick={() => openPage(page.key as typeof currentPage)}
                                    >
                                        <span className="mr-2">{(page as typeof pages[0]).icon}</span>
                                        <span>{page.name}</span>
                                    </button>
                                </Menu.Item>
                            )
                        ))}
                        
                        <Divider className="my-1 mx-2" />

                        <Menu.Item className="flex-shrink-0">
                            <button 
                                className="py-2 px-4 w-full text-left my-1 flex items-center"
                                data-umami-event="Logout"
                                onClick={logout}
                            >
                                <span className="mr-2">
                                    <LogOut className="h-5 w-5" />
                                </span>
                                <span>{t('pages.profilePage.logout')}</span>
                            </button>
                        </Menu.Item>
                        
                        <Menu.Item className="flex-shrink-0">
                            <FeedbackDialog currentPage={currentPage} />
                        </Menu.Item>
                    </Menu>
                </nav>
                <div className="flex-1 relative" ref={pageContentRef}>
                    <div className="mx-auto max-w-[500px] px-4">
                        <div className="flex flex-col gap-4 my-8">
                            <ErrorBoundary>{renderPage()}</ErrorBoundary>
                        </div>
                        
                        {hasPendingChanges && (
                            <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2">
                                <Button
                                    color="primary"
                                    onClick={handleSave}
                                    className="shadow-lg"
                                    data-umami-event="Profile Save"
                                    data-testid="profile-save-button"
                                >
                                    {t('pages.profilePage.saveChanges')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AppPageLayout>
    );
}

export default ProfilePage;
